/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useClerk,
  useAuth as useClerkAuth,
  useSession,
  useSignIn,
  useSignUp,
  useUser,
} from '@clerk/clerk-react';

import { useToast } from '@/hooks/use-toast';
import '@/hooks/usePostHogTracking';
import { createSupabaseClient } from '@/integrations/supabase/client';
import type { TablesInsert } from '@/integrations/supabase/types';
import { getSubscriptionPreferences } from '@/lib/storageKeys';
import { linkAnonymousConsentLogsToUser } from '@/services/CookieConsentService';
import { postHogTrackingService } from '@/services/PostHogTrackingService';
import { referralAttributionService } from '@/services/ReferralAttributionService';
import {
  SubscriptionTier,
  SubscriptionTrialIneligibilityReason,
} from '@/types/subscription';
import { Profile, ProfileUpdate } from '@/types/supabase-custom';
import { UserCurrency } from '@/types/user';

export interface AuthUser {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  primaryEmailAddress?: {
    emailAddress: string;
  } | null;
  imageUrl?: string | null;
}

interface AuthSession {
  id: string;
}

export interface AuthContextType {
  user: AuthUser | null;
  session: AuthSession | null;
  profile: Profile | null;
  loading: boolean;
  isLoaded: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  requestPasswordReset: (email: string) => Promise<{ error: Error | null }>;
  resetPassword: (
    password: string,
    code: string
  ) => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    termsAccepted?: boolean
  ) => Promise<{
    error: Error | null;
    needsVerification?: boolean;
    email?: string;
  }>;
  signUpWithInvitation: (
    token: string,
    password: string,
    firstName?: string,
    lastName?: string,
    termsAccepted?: boolean
  ) => Promise<{
    error: Error | null;
    needsVerification?: boolean;
    email?: string;
  }>;
  signOut: () => Promise<{ error: Error | null }>;
  updateProfile: (
    updates: ProfileUpdate,
    options?: { suppressSuccessToast?: boolean }
  ) => Promise<{ error: Error | null }>;
  showMigrationAlert: boolean;
  setShowMigrationAlert: (show: boolean) => void;
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

interface ClerkError {
  errors?: Array<{
    code?: string;
    message?: string;
  }>;
  message?: string;
}

const isClerkError = (error: unknown): error is ClerkError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('errors' in error || 'message' in error)
  );
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (isClerkError(error)) {
    return error.errors?.[0]?.message || error.message || 'An error occurred';
  }
  return 'An error occurred';
};

const isNetworkError = (error: unknown): boolean => {
  const errorMessage =
    error instanceof Error
      ? error.message
      : isClerkError(error)
        ? error.errors?.[0]?.message || error.message || ''
        : String(error);

  const lowerErrorMessage = errorMessage.toLowerCase();

  return (
    lowerErrorMessage.includes('failed to fetch') ||
    lowerErrorMessage.includes('network') ||
    lowerErrorMessage.includes('offline') ||
    lowerErrorMessage.includes('timeout') ||
    lowerErrorMessage.includes('econnrefused') ||
    lowerErrorMessage.includes('err_network')
  );
};

type SignInErrorKind = 'email-not-found' | 'invalid-password' | 'generic';

const isEnumerationError = (error: unknown): boolean => {
  if (!isClerkError(error)) {
    return false;
  }

  const errorCode = error.errors?.[0]?.code;
  const enumerationCodes: string[] = ['form_identifier_not_found'];

  return enumerationCodes.includes(errorCode || '');
};

const getSignInErrorKind = (error: unknown): SignInErrorKind => {
  if (!isClerkError(error)) {
    return 'generic';
  }

  const errorCode = error.errors?.[0]?.code;

  if (errorCode === 'form_identifier_not_found') {
    return 'email-not-found';
  }

  if (errorCode === 'form_password_incorrect') {
    return 'invalid-password';
  }

  return 'generic';
};

export const AuthProvider = ({ children }: Readonly<AuthProviderProps>) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMigrationAlert, setShowMigrationAlert] = useState(false);
  const [isInvitationSignup, setIsInvitationSignup] = useState(false);
  const domainOrganizationAccessAttemptedUserIds = useRef(new Set<string>());

  const { user } = useUser();
  const { session } = useSession();
  const {
    isLoaded,
    isSignedIn,
    signOut: clerkSignOut,
    getToken,
    orgId: activeOrgId,
  } = useClerkAuth();
  const { setActive: setActiveOrganization } = useClerk();
  const { signIn: clerkSignIn, isLoaded: signInLoaded } = useSignIn();
  const {
    signUp: clerkSignUp,
    isLoaded: signUpLoaded,
    setActive,
  } = useSignUp();
  const { toast } = useToast();

  const supabaseWithAuth = useMemo(
    () => createSupabaseClient(getToken),
    [getToken]
  );

  const createProfile = useCallback(
    async (userData: AuthUser) => {
      const { selectedTier, userCurrency } = getSubscriptionPreferences();

      const trialIneligibilityReason: SubscriptionTrialIneligibilityReason =
        (() => {
          if (selectedTier === SubscriptionTier.Team) {
            return SubscriptionTrialIneligibilityReason.TeamOwner;
          }

          if (isInvitationSignup) {
            return SubscriptionTrialIneligibilityReason.JoinedTeamFirst;
          }

          return null;
        })();

      const newProfileData: TablesInsert<'profiles'> = {
        user_id: userData.id,
        email: userData.primaryEmailAddress?.emailAddress || '',
        first_name: userData.firstName || '',
        last_name: userData.lastName || '',
        profile_photo_url: userData.imageUrl || null,
        currency: userCurrency || UserCurrency.USD,
        trial_ineligibility_reason: trialIneligibilityReason,
        privacy_policy_accepted: true,
        terms_accepted: true,
        terms_accepted_at: new Date().toISOString(),
        privacy_policy_accepted_at: new Date().toISOString(),
      };

      const { data: createdProfile, error: createError } =
        await supabaseWithAuth
          .from('profiles')
          .insert(newProfileData)
          .select()
          .single();

      if (createError) {
        throw createError;
      }

      return createdProfile;
    },
    [supabaseWithAuth, isInvitationSignup]
  );

  const attemptDomainOrganizationAssignment = useCallback(async () => {
    const TIMEOUT_MS = 10_000;
    const { data, error } = await supabaseWithAuth.functions.invoke(
      'assign-domain-organization-access',
      { signal: AbortSignal.timeout(TIMEOUT_MS) }
    );

    if (error || data?.outcome === 'failed') {
      throw new Error(`Assignment failed: ${error?.message ?? data?.outcome}`);
    }

    if (data?.outcome === 'assigned' || data?.outcome === 'already_member') {
      const isAlreadyActive = activeOrgId === data?.organizationId;

      if (!isAlreadyActive && data?.organizationId) {
        await setActiveOrganization({ organization: data.organizationId });
      }
      await getToken({ skipCache: true });
      window.dispatchEvent(new CustomEvent('org-session-refreshed'));
      return;
    }

    if (data?.outcome === 'unmatched_email_domain') {
      return;
    }

    throw new Error(`Unexpected outcome: ${data?.outcome}`);
  }, [activeOrgId, getToken, setActiveOrganization, supabaseWithAuth]);

  const assignDomainOrganizationAccess = useCallback(
    async (userData: AuthUser) => {
      if (domainOrganizationAccessAttemptedUserIds.current.has(userData.id)) {
        return;
      }

      domainOrganizationAccessAttemptedUserIds.current.add(userData.id);

      const MAX_RETRIES = 2;
      const BASE_DELAY_MS = 2_000;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          await attemptDomainOrganizationAssignment();
          return;
        } catch (error) {
          const isLastAttempt = attempt === MAX_RETRIES;

          if (isLastAttempt) {
            console.error(
              'Failed to assign domain organization access after retries:',
              error
            );

            domainOrganizationAccessAttemptedUserIds.current.delete(
              userData.id
            );

            toast({
              title: 'Organization setup failed',
              description:
                'We could not set up your organization access. Please refresh the page or contact support.',
              variant: 'destructive',
            });

            return;
          }

          const delay = BASE_DELAY_MS * 2 ** attempt;
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    },
    [attemptDomainOrganizationAssignment, toast]
  );

  const updateProfile = useCallback(
    async (
      updates: Partial<Profile>,
      options: { suppressSuccessToast?: boolean } = {}
    ) => {
      if (!user) {
        return { error: new Error('No user logged in') };
      }

      const secureUpdates = { ...updates };
      delete secureUpdates.role;

      try {
        const { error } = await supabaseWithAuth
          .from('profiles')
          .update(secureUpdates)
          .eq('user_id', user.id);

        if (error) {
          toast({
            variant: 'destructive',
            title: 'Update failed',
            description: error.message,
          });
          return { error };
        } else {
          const { data: profileData } = await supabaseWithAuth
            .from('profiles')
            .select('*')
            .eq('user_id', user.id)
            .single();

          setProfile(profileData as Profile);

          if (!options.suppressSuccessToast) {
            toast({
              title: 'Profile updated',
              description: 'Your profile has been updated successfully',
            });
          }
        }

        return { error: null };
      } catch (error: unknown) {
        toast({
          variant: 'destructive',
          title: 'Update failed',
          description: getErrorMessage(error),
        });

        return {
          error:
            error instanceof Error ? error : new Error(getErrorMessage(error)),
        };
      }
    },
    [user, supabaseWithAuth, toast]
  );

  const fetchProfile = useCallback(
    async (userData: AuthUser) => {
      try {
        let { data: profileData } = await supabaseWithAuth
          .from('profiles')
          .select('*')
          .eq('user_id', userData.id)
          .maybeSingle();

        if (!profileData) {
          profileData = await createProfile(userData);
        }

        if (
          Boolean(profileData.terms_accepted) === false ||
          Boolean(profileData.privacy_policy_accepted) === false
        ) {
          await updateProfile(
            {
              ...profileData,
              terms_accepted: true,
              privacy_policy_accepted: true,
              terms_accepted_at: new Date().toISOString(),
              privacy_policy_accepted_at: new Date().toISOString(),
            } as ProfileUpdate,
            { suppressSuccessToast: true }
          );
        }

        setProfile({
          ...profileData,
          terms_accepted: true,
          privacy_policy_accepted: true,
        } as Profile);

        await assignDomainOrganizationAccess(userData);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching/creating profile:', error);
        }
      }
    },

    [
      supabaseWithAuth,
      createProfile,
      updateProfile,
      assignDomainOrganizationAccess,
    ]
  );

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (isSignedIn && user) {
      fetchProfile(user as unknown as AuthUser);
    } else {
      setProfile(null);
    }

    setLoading(false);
  }, [isLoaded, isSignedIn, user, session, fetchProfile]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (isSignedIn && user && profile && getToken) {
      linkAnonymousConsentLogsToUser(user.id, getToken);
      postHogTrackingService.identifyUser(user);
      referralAttributionService.applyToPostHog();
    }

    if (!isSignedIn) {
      postHogTrackingService.reset();
    }
  }, [getToken, isLoaded, isSignedIn, profile, user]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      if (!signInLoaded || !clerkSignIn) {
        return { error: new Error('Sign in not ready') };
      }

      try {
        setShowMigrationAlert(false);
        const result = await clerkSignIn.create({
          identifier: email,
          password: password,
        });

        if (result.status === 'complete') {
          window.location.href = '/home';
          return { error: null };
        } else {
          return { error: new Error('Sign in incomplete') };
        }
      } catch (error: unknown) {
        const signInErrorKind = getSignInErrorKind(error);

        if (
          signInErrorKind === 'email-not-found' ||
          signInErrorKind === 'invalid-password'
        ) {
          return {
            error: new Error('Invalid email or password.'),
          };
        }

        return {
          error: new Error('Sign in failed. Please try again.'),
        };
      }
    },
    [signInLoaded, clerkSignIn]
  );

  const handlePasswordResetError = useCallback(
    (error: unknown) => {
      console.error('Password reset error:', error);

      if (isEnumerationError(error)) {
        toast({
          title: 'Password reset',
          description:
            'If an account exists with this email, you will receive a password reset code.',
        });
        return {
          error:
            error instanceof Error ? error : new Error(getErrorMessage(error)),
        };
      }

      if (isNetworkError(error)) {
        toast({
          variant: 'destructive',
          title: 'Password reset failed',
          description:
            'Unable to send password reset email. Please check your connection and try again.',
        });
        return {
          error:
            error instanceof Error ? error : new Error(getErrorMessage(error)),
        };
      }

      toast({
        variant: 'destructive',
        title: 'Password reset failed',
        description: 'Unable to send password reset email. Please try again.',
      });

      return {
        error:
          error instanceof Error ? error : new Error(getErrorMessage(error)),
      };
    },
    [toast]
  );

  const requestPasswordReset = useCallback(
    async (email: string) => {
      if (!signInLoaded || !clerkSignIn) {
        return { error: new Error('Sign in not ready') };
      }

      try {
        await clerkSignIn.create({
          strategy: 'reset_password_email_code',
          identifier: email,
        });

        toast({
          title: 'Password reset',
          description:
            'If an account exists with this email, you will receive a password reset code.',
        });

        return { error: null };
      } catch (error) {
        return handlePasswordResetError(error);
      }
    },
    [signInLoaded, clerkSignIn, toast, handlePasswordResetError]
  );

  const resetPassword = useCallback(
    async (password: string, code: string) => {
      if (!signInLoaded || !clerkSignIn) {
        return { error: new Error('Sign in not ready') };
      }

      try {
        const result = await clerkSignIn.attemptFirstFactor({
          strategy: 'reset_password_email_code',
          code,
          password,
        });

        await setActive({ session: result.createdSessionId });

        toast({
          title: 'Password reset',
          description: 'Your password has been reset successfully.',
        });

        return { error: null };
      } catch (error: unknown) {
        const message = getErrorMessage(error);

        toast({
          variant: 'destructive',
          title: 'Password reset failed',
          description: message,
        });

        console.error('Sign in error:', message);

        return {
          error:
            error instanceof Error ? error : new Error(getErrorMessage(error)),
        };
      }
    },
    [signInLoaded, clerkSignIn, toast, setActive]
  );

  const signUpWithInvitation = useCallback(
    async (
      token: string,
      password: string,
      firstName?: string,
      lastName?: string,
      termsAccepted?: boolean
    ) => {
      if (!signUpLoaded || !clerkSignUp) {
        return { error: new Error('Sign up not ready') };
      }

      if (!termsAccepted) {
        toast({
          variant: 'destructive',
          title: 'Terms acceptance required',
          description:
            'You must accept the Terms & Conditions and Privacy Policy to sign up.',
        });
        return { error: new Error('Terms not accepted') };
      }

      try {
        const result = await clerkSignUp.create({
          strategy: 'ticket',
          ticket: token,
          password,
          firstName: firstName || '',
          lastName: lastName || '',
        });

        if (result.status === 'complete') {
          setIsInvitationSignup(true);

          toast({
            title: 'Account created successfully',
            description:
              'Welcome to Praxis! You can now start using the platform.',
          });

          await setActive({ session: result.createdSessionId });

          return { error: null };
        } else {
          return { error: new Error('Sign up incomplete') };
        }
      } catch (error: unknown) {
        toast({
          variant: 'destructive',
          title: 'Sign up failed',
          description: 'An error occurred while signing up.',
        });

        return {
          error:
            error instanceof Error ? error : new Error(getErrorMessage(error)),
        };
      }
    },
    [signUpLoaded, clerkSignUp, toast, setActive]
  );

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      firstName?: string,
      lastName?: string,
      termsAccepted?: boolean
    ) => {
      if (!signUpLoaded || !clerkSignUp) {
        return { error: new Error('Sign up not ready') };
      }

      if (!termsAccepted) {
        toast({
          variant: 'destructive',
          title: 'Terms acceptance required',
          description:
            'You must accept the Terms & Conditions and Privacy Policy to sign up.',
        });
        return { error: new Error('Terms not accepted') };
      }

      try {
        const result = await clerkSignUp.create({
          emailAddress: email,
          password: password,
          firstName: firstName || '',
          lastName: lastName || '',
        });

        if (result.status === 'complete') {
          toast({
            title: 'Account created successfully',
            description:
              'Welcome to Praxis! You can now start using the platform.',
          });

          return { error: null };
        } else if (result.status === 'missing_requirements') {
          await clerkSignUp.prepareEmailAddressVerification({
            strategy: 'email_code',
          });

          return { error: null, needsVerification: true, email };
        } else {
          return { error: new Error('Sign up incomplete') };
        }
      } catch (error: unknown) {
        console.error('Sign up error:', error);

        toast({
          variant: 'destructive',
          title: 'Sign up failed',
          description:
            'Unable to create account. Please try again or sign in if you already have an account.',
        });

        return {
          error: new Error('Unable to create account'),
        };
      }
    },

    [signUpLoaded, clerkSignUp, toast]
  );

  const signOut = useCallback(async () => {
    try {
      postHogTrackingService.reset();

      await clerkSignOut();
      setProfile(null);
      return { error: null };
    } catch (error: unknown) {
      setProfile(null);

      toast({
        variant: 'destructive',
        title: 'Sign out failed',
        description: getErrorMessage(error),
      });

      return {
        error:
          error instanceof Error ? error : new Error(getErrorMessage(error)),
      };
    }
  }, [clerkSignOut, toast]);

  const getTokenWithOptions = useCallback(
    async (options?: { skipCache?: boolean }) => {
      return getToken(options);
    },
    [getToken]
  );

  const value = useMemo(
    () => ({
      user: user as unknown as AuthUser | null,
      session: session as AuthSession | null,
      profile,
      loading,
      isLoaded,
      signIn,
      requestPasswordReset,
      resetPassword,
      signUpWithInvitation,
      signUp,
      signOut,
      updateProfile,
      showMigrationAlert,
      setShowMigrationAlert,
      getToken: getTokenWithOptions,
    }),
    [
      user,
      session,
      profile,
      loading,
      isLoaded,
      signIn,
      requestPasswordReset,
      resetPassword,
      signUpWithInvitation,
      signUp,
      signOut,
      updateProfile,
      showMigrationAlert,
      getTokenWithOptions,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'useAuth called outside of AuthProvider. Returning safe fallbacks.'
      );
    }
    const fallback: AuthContextType = {
      user: null,
      session: null,
      profile: null,
      loading: true,
      isLoaded: false,
      signIn: async () => ({ error: new Error('AuthProvider missing') }),
      requestPasswordReset: async () => ({
        error: new Error('AuthProvider missing'),
      }),
      resetPassword: async () => ({
        error: new Error('AuthProvider missing'),
      }),
      signUp: async () => ({ error: new Error('AuthProvider missing') }),
      signUpWithInvitation: async () => ({
        error: new Error('AuthProvider missing'),
      }),
      signOut: async () => ({ error: new Error('AuthProvider missing') }),
      updateProfile: async () => ({ error: new Error('AuthProvider missing') }),
      showMigrationAlert: false,
      setShowMigrationAlert: () => {},
      getToken: async () => null,
    };
    return fallback;
  }
  return context;
};
